import { useEffect } from 'react';
import { fetchAppVersion } from '../services/firestoreService';

// ── ストレージキー ─────────────────────────────────────────────────
/** Firestore から取得したデプロイ日時を保持 */
const LS_KEY_DEPLOYED_AT = 'app_deployed_at';
/** 最後に Firestore へ問い合わせた時刻（ms） */
const LS_KEY_LAST_CHECKED = 'app_version_last_checked';
/** セッション中にチェック済みかどうか（再表示防止） */
const SS_KEY_CHECKED = 'app_version_checked';

/** Firestore 問い合わせ間隔: 24時間 */
const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

// ── モジュールレベルのフラグ ───────────────────────────────────────
/**
 * 並行実行防止フラグ
 * React の StrictMode やコンポーネントの素早い re-mount で
 * useEffect が複数回走っても Firestore 読み取りを1回に抑える
 */
let isChecking = false;

// ── 環境判定 ───────────────────────────────────────────────────────
/**
 * ローカル開発環境かどうかを判定
 * localhost / 127.0.0.1 の場合はバージョンチェックをスキップする
 */
const isLocalhost = () =>
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

/**
 * useVersionCheck - Firestore を使ったバージョンチェックフック
 *
 * 動作:
 *   1. ローカル環境（localhost / 127.0.0.1）はスキップ
 *   2. セッション中に既にチェック済みの場合はスキップ
 *   3. 並行実行中の場合はスキップ（重複 Firestore 読み取り防止）
 *   4. 24時間以内に Firestore 確認済みであれば再確認をスキップ
 *   5. Firestore の deployedAt と localStorage の値を比較
 *      - 初回訪問（localStorage なし）: deployedAt を保存して終了（更新不要）
 *      - deployedAt が一致: 最新版のためスキップ
 *      - deployedAt が不一致: confirm を表示し、OK なら localStorage をクリアしてリロード
 *
 * 使用箇所:
 *   NationalMapPage (/map)、FourGodsPage (/4gods/:godId)、
 *   FiveKirinPage (/4gods/5kirin5) にて、画面を開くたびに1回実行
 */
const useVersionCheck = () => {
  useEffect(() => {
    // ── ローカル環境はスキップ ──────────────────────────────
    if (isLocalhost()) {
      console.debug('[VersionCheck] ローカル環境のためスキップ');
      return;
    }

    // ── セッション中は1回のみ実行 ──────────────────────────
    if (sessionStorage.getItem(SS_KEY_CHECKED)) {
      console.debug('[VersionCheck] セッション中チェック済みのためスキップ');
      return;
    }

    // ── 並行実行防止（React StrictMode など） ──────────────
    if (isChecking) {
      console.debug('[VersionCheck] チェック実行中のためスキップ');
      return;
    }

    const check = async () => {
      isChecking = true;
      try {
        const cachedDeployedAt = localStorage.getItem(LS_KEY_DEPLOYED_AT);
        const lastCheckedAt    = localStorage.getItem(LS_KEY_LAST_CHECKED);
        const now              = Date.now();

        // ── 24時間以内にチェック済みなら Firestore 問い合わせをスキップ ──
        const isRecentlyChecked =
          cachedDeployedAt &&
          lastCheckedAt &&
          now - Number(lastCheckedAt) < CHECK_INTERVAL_MS;

        if (isRecentlyChecked) {
          console.debug('[VersionCheck] 24時間以内にチェック済みのためスキップ:', cachedDeployedAt);
          sessionStorage.setItem(SS_KEY_CHECKED, '1');
          return;
        }

        // ── Firestore からデプロイ日時を取得 ────────────────
        console.debug('[VersionCheck] Firestoreへバージョン確認中...');
        const { deployedAt } = await fetchAppVersion();

        if (!deployedAt) {
          // Firestore にバージョン情報が未登録（初期状態）
          console.debug('[VersionCheck] Firestoreにバージョン情報なし（スキップ）');
          sessionStorage.setItem(SS_KEY_CHECKED, '1');
          return;
        }

        // lastCheckedAt を常に更新
        localStorage.setItem(LS_KEY_LAST_CHECKED, String(now));

        if (!cachedDeployedAt) {
          // ── 初回訪問: キャッシュなし → 最新版をそのまま保存 ──
          localStorage.setItem(LS_KEY_DEPLOYED_AT, deployedAt);
          console.debug('[VersionCheck] 初回訪問 → バージョン保存:', deployedAt);
        } else if (cachedDeployedAt !== deployedAt) {
          // ── バージョン不一致: 更新を促す ───────────────────
          console.debug(
            `[VersionCheck] バージョン不一致: cached=${cachedDeployedAt}, remote=${deployedAt}`
          );

          const ok = window.confirm(
            'アプリが更新されました。\n' +
            '最新バージョンに更新するには「OK」を押してください。\n\n' +
            '（ページを自動的に再読み込みします）'
          );

          if (ok) {
            console.debug('[VersionCheck] ユーザーが更新を選択 → リロード');
            // ローカルストレージをリセットして新バージョンを初回扱いにする
            localStorage.removeItem(LS_KEY_DEPLOYED_AT);
            localStorage.removeItem(LS_KEY_LAST_CHECKED);
            sessionStorage.removeItem(SS_KEY_CHECKED);
            window.location.reload();
            return; // reload 後は以降の処理は不要
          } else {
            // キャンセル: 新しい deployedAt をローカルに保存し、
            // セッション中はコンファームを再表示しない
            console.debug('[VersionCheck] ユーザーがスキップ → バージョンを更新して継続');
            localStorage.setItem(LS_KEY_DEPLOYED_AT, deployedAt);
          }
        } else {
          console.debug('[VersionCheck] バージョン一致: 最新版を使用中:', deployedAt);
        }

        sessionStorage.setItem(SS_KEY_CHECKED, '1');
      } catch (err) {
        // ネットワークエラー等は無視してアプリを継続
        console.error('[VersionCheck] バージョンチェックエラー（無視）:', err.message);
      } finally {
        isChecking = false;
      }
    };

    check();
  }, []); // 画面を開くたびに1回実行（マウント時）
};

export default useVersionCheck;
